import {
  BaggagePolicyDetailItem,
  BaggagePolicyDetails,
  BaggagePolicyRestrictions,
  CheckInBaggagePolicies,
  CheckInExcessBaggagePrice,
} from '@dapi/sdk';
import {
  combineCheckInBaggagePolicies,
  createCheckInBaggagePolicies,
} from '@dapi/sdk/spec';
import {
  currency,
  journeyTraveler,
} from '../dictionaries';
import {
  bike,
  buggyGate,
  checkInHandLuggage,
  specialEquipment,
  weapon,
  wheelchair,
} from '../dictionaries/baggageItem';

export const bagQuantity = 1;
export const excessBagsPrice = 6000;
export const excessBagsCurrCode = 'EUR';
export const sizeValue = '180';
export const sizeUnit = 'centimeter';
export const sizeQualifier = 'maxLength';
export const weightValue = '23';
export const weightUnit = 'kilogram';
export const weightQualifier = 'upTo';

export const bagPolicyRestrictionsSize: BaggagePolicyRestrictions = {
  type: 'size',
  value: sizeValue,
  unit: sizeUnit,
  qualifier: sizeQualifier
};

export const bagPolicyRestrictionsWeight: BaggagePolicyRestrictions = {
  type: 'weight',
  value: weightValue,
  unit: weightUnit,
  qualifier: weightQualifier
};

export const bagCharacteristicsWeight: BaggagePolicyDetailItem = {
  policyDetails: [bagPolicyRestrictionsWeight]
};

export const bagAllowance: BaggagePolicyDetails = {
  quantity: bagQuantity,
  baggageCharacteristics: [bagCharacteristicsWeight]
};

export const noBagAllowance: BaggagePolicyDetails = {
  quantity: 0,
  baggageCharacteristics: [bagCharacteristicsWeight]
};

export const bagAllowanceOnlyWeight: BaggagePolicyDetails = {
  weightAmount: weightValue,
  weightUnit: weightUnit
};

export const excessBags: CheckInExcessBaggagePrice = {
  ordinalNumber: 1,
  price: {
    value: excessBagsPrice,
    currencyCode: excessBagsCurrCode,
    currency: currency
  }
};

export const defaultCheckInBaggagePolicies: CheckInBaggagePolicies = createCheckInBaggagePolicies({
  template: {
    travelerIds: [journeyTraveler.id],
    adultBaggageAllowance: bagAllowance,
    allowedSpecialBags: [bike, buggyGate, checkInHandLuggage, weapon, wheelchair, specialEquipment],
    cabinAllowance: bagAllowance,
    excessBaggagePrices: [excessBags],
    prepaidAllowance: bagAllowance,
    previouslyPaidDiscount: bagAllowance,
    totalAllowance: bagAllowance
  }
});

export const defaultCheckInBaggagePoliciesFor2Adt: CheckInBaggagePolicies = createCheckInBaggagePolicies({
  template: {
    travelerIds: ['JOURNEY-TRAVELER-0-MOCK', 'JOURNEY-TRAVELER-1-MOCK'],
    adultBaggageAllowance: bagAllowance,
    allowedSpecialBags: [bike, buggyGate, checkInHandLuggage, weapon, wheelchair, specialEquipment],
    cabinAllowance: bagAllowance,
    excessBaggagePrices: [excessBags],
    prepaidAllowance: bagAllowance,
    previouslyPaidDiscount: bagAllowance,
    totalAllowance: bagAllowance
  }
});

export const defaultCheckInBaggagePoliciesFor3Adt: CheckInBaggagePolicies = createCheckInBaggagePolicies({
  template: {
    travelerIds: ['JOURNEY-TRAVELER-0-MOCK', 'JOURNEY-TRAVELER-1-MOCK', 'JOURNEY-TRAVELER-2-MOCK'],
    adultBaggageAllowance: bagAllowance,
    allowedSpecialBags: [bike, buggyGate, checkInHandLuggage, weapon, wheelchair, specialEquipment],
    cabinAllowance: bagAllowance,
    excessBaggagePrices: [excessBags],
    prepaidAllowance: bagAllowance,
    previouslyPaidDiscount: bagAllowance,
    totalAllowance: bagAllowance
  }
});

export const defaultTwoCheckInBaggagePoliciesWithNoPaidBagAllowance: CheckInBaggagePolicies[] = combineCheckInBaggagePolicies([
  createCheckInBaggagePolicies(
    {
      template: {
        travelerIds: ['JOURNEY-TRAVELER-0-MOCK'],
        adultBaggageAllowance: bagAllowance,
        excessBaggagePrices: [excessBags],
        totalAllowance: bagAllowance
      }
    }),
  createCheckInBaggagePolicies(
    {
      template: {
        travelerIds: ['JOURNEY-TRAVELER-1-MOCK'],
        adultBaggageAllowance: bagAllowance,
        excessBaggagePrices: [excessBags],
        totalAllowance: bagAllowance
      }
    })
]).items;

export const checkInBaggagePoliciesWithNoFreeBagAllowance: CheckInBaggagePolicies = createCheckInBaggagePolicies({
  template: {
    travelerIds: [journeyTraveler.id],
    adultBaggageAllowance: noBagAllowance,
    allowedSpecialBags: [bike, buggyGate, checkInHandLuggage, weapon, wheelchair, specialEquipment],
    cabinAllowance: noBagAllowance,
    excessBaggagePrices: [excessBags],
    prepaidAllowance: noBagAllowance,
    previouslyPaidDiscount: noBagAllowance,
    totalAllowance: noBagAllowance
  }
});

export const pieceCheckInBaggagePolicies: CheckInBaggagePolicies = createCheckInBaggagePolicies({
  template: {
    travelerIds: [journeyTraveler.id],
    adultBaggageAllowance: bagAllowance,
    allowedSpecialBags: [bike, buggyGate, checkInHandLuggage, weapon, wheelchair, specialEquipment],
    fareTypeAllowance: bagAllowance,
    discretionaryWaiver: bagAllowance,
    frequentFlyerBaggageAllowance: bagAllowance,
    excessBaggagePrices: [excessBags],
    prepaidAllowance: bagAllowance,
    previouslyPaidDiscount: bagAllowance,
    totalAllowance: bagAllowance
  }
});

export const weightCheckInBaggagePolicies: CheckInBaggagePolicies = createCheckInBaggagePolicies({
  template: {
    travelerIds: [journeyTraveler.id],
    adultBaggageAllowance: bagAllowanceOnlyWeight,
    allowedSpecialBags: [bike, buggyGate, checkInHandLuggage, weapon, wheelchair, specialEquipment],
    fareTypeAllowance: bagAllowanceOnlyWeight,
    discretionaryWaiver: bagAllowanceOnlyWeight,
    frequentFlyerBaggageAllowance: bagAllowanceOnlyWeight,
    excessBaggagePrices: [excessBags],
    prepaidAllowance: bagAllowanceOnlyWeight,
    previouslyPaidDiscount: bagAllowanceOnlyWeight,
    totalAllowance: bagAllowanceOnlyWeight
  }
});

export const defaultTwoCheckInBaggagePolicies: CheckInBaggagePolicies[] = combineCheckInBaggagePolicies([
  createCheckInBaggagePolicies(
    {
      template: {
        travelerIds: ['JOURNEY-TRAVELER-0-MOCK'],
        adultBaggageAllowance: bagAllowance,
        allowedSpecialBags: [bike, buggyGate, checkInHandLuggage, weapon, wheelchair, specialEquipment],
        cabinAllowance: bagAllowance,
        excessBaggagePrices: [excessBags],
        prepaidAllowance: bagAllowance,
        previouslyPaidDiscount: bagAllowance,
        totalAllowance: bagAllowance
      }
    }),
  createCheckInBaggagePolicies(
    {
      template: {
        travelerIds: ['JOURNEY-TRAVELER-1-MOCK'],
        adultBaggageAllowance: bagAllowance,
        allowedSpecialBags: [bike, buggyGate, checkInHandLuggage, weapon, wheelchair, specialEquipment],
        cabinAllowance: bagAllowance,
        excessBaggagePrices: [excessBags],
        prepaidAllowance: bagAllowance,
        previouslyPaidDiscount: bagAllowance,
        totalAllowance: bagAllowance
      }
    })
]).items;

export const checkInBaggagePoliciesForAdtChdInf: CheckInBaggagePolicies[] = combineCheckInBaggagePolicies([
  createCheckInBaggagePolicies(
    {
      template: {
        travelerIds: ['JOURNEY-TRAVELER-0-MOCK'],
        adultBaggageAllowance: bagAllowance,
        excessBaggagePrices: [excessBags],
        totalAllowance: bagAllowance
      }
    }),
  createCheckInBaggagePolicies(
    {
      template: {
        travelerIds: ['JOURNEY-TRAVELER-1-MOCK'],
        adultBaggageAllowance: bagAllowance,
        excessBaggagePrices: [excessBags],
        totalAllowance: bagAllowance
      }
    }),
  createCheckInBaggagePolicies(
    {
      template: {
        travelerIds: ['JOURNEY-TRAVELER-3-MOCK'],
        adultBaggageAllowance: bagAllowance,
        excessBaggagePrices: [excessBags],
        totalAllowance: bagAllowance
      }
    })
]).items;
